/**
 * Focus mode of the chat (Inicio v2, spec S3): the conversations (`clientId`)
 * of the person's tasks in progress and whether the chat shows only them.
 * Purely client-side: the backend never filters.
 *
 * - Inicio enters focus on «Tomar» and «Ir al chat» with every conversation
 *   in progress (views/dashboard/inicio/hooks/use-task-focus.js).
 * - The chat keeps the list current (Pusher `taskUpdated` carries `clientId`)
 *   with `setTaskFocusConversations`, and leaves with `exitTaskFocus`.
 * Exiting keeps the list, so the chat can still show «Mis tareas · N» and
 * enter again without ids.
 *
 * Action payloads carry only conversation ids (no names or messages), so the
 * Redux Sentry breadcrumbs stay free of PII.
 */

export const TASK_FOCUS_INITIAL_STATE = Object.freeze({ active: false, conversationIds: [] });

const ENTER = 'taskFocus/ENTER';
const SET_CONVERSATIONS = 'taskFocus/SET_CONVERSATIONS';
const EXIT = 'taskFocus/EXIT';

/** Non-empty strings, each once, in their first order. */
const cleanIds = (ids) => {
  if (!Array.isArray(ids)) return [];
  const out = [];
  ids.forEach((id) => {
    if (typeof id === 'string' && id.trim() && out.indexOf(id) === -1) out.push(id);
  });
  return out;
};

const sameIds = (a, b) => a.length === b.length && a.every((id) => b.indexOf(id) !== -1);

/** Keeps the previous array (and the state object) when the ids did not change. */
const withIds = (state, ids) => (sameIds(state.conversationIds, ids) ? state.conversationIds : ids);

export default function taskFocusReducer(state = TASK_FOCUS_INITIAL_STATE, action) {
  const { type, payload } = action;
  switch (type) {
    case ENTER: {
      const conversationIds = payload === undefined ? state.conversationIds : withIds(state, cleanIds(payload));
      if (state.active && conversationIds === state.conversationIds) return state;
      return { active: true, conversationIds };
    }
    case SET_CONVERSATIONS: {
      const conversationIds = withIds(state, cleanIds(payload));
      if (conversationIds === state.conversationIds) return state;
      return { ...state, conversationIds };
    }
    case EXIT:
      return state.active ? { ...state, active: false } : state;
    default:
      return state;
  }
}

/** Turns focus on; with `conversationIds`, also replaces the list. */
export const enterTaskFocus = (conversationIds) => ({ type: ENTER, payload: conversationIds });

/** Replaces the list of conversations in progress; focus stays as it was. */
export const setTaskFocusConversations = (conversationIds) => ({ type: SET_CONVERSATIONS, payload: conversationIds });

/** Turns focus off and keeps the list. */
export const exitTaskFocus = () => ({ type: EXIT });

const sliceOf = (state) => (state && state.taskFocus) || TASK_FOCUS_INITIAL_STATE;

export const selectTaskFocus = (state) => sliceOf(state);
export const selectIsTaskFocusActive = (state) => sliceOf(state).active === true;
export const selectTaskFocusConversationIds = (state) => sliceOf(state).conversationIds;
